import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollText, RefreshCw, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AuditEntry {
  id: string;
  user_id: string | null;
  user_email: string | null;
  table_name: string;
  record_id: string | null;
  action: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  old_data: any;
  new_data: any;
  created_at: string;
}

const TABLE_LABELS: Record<string, string> = {
  prestations: 'Prestation',
  clients: 'Client',
  artistes: 'Artiste',
  factures: 'Facture',
};

const ACTION_LABELS: Record<string, string> = {
  INSERT: 'Création',
  UPDATE: 'Modification',
  DELETE: 'Suppression',
};

const ACTION_COLORS: Record<string, string> = {
  INSERT: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  UPDATE: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  DELETE: 'bg-red-500/15 text-red-400 border-red-500/30',
};

function describeChanges(entry: AuditEntry): string {
  if (entry.action === 'INSERT' && entry.new_data) {
    const label = entry.new_data.numero || entry.new_data.nom_scene || entry.new_data.nom || entry.new_data.client_nom || entry.new_data.libelle;
    return label ? `Création de « ${label} »` : 'Nouvel enregistrement';
  }
  if (entry.action === 'DELETE' && entry.old_data) {
    const label = entry.old_data.numero || entry.old_data.nom_scene || entry.old_data.nom || entry.old_data.client_nom || entry.old_data.libelle;
    return label ? `Suppression de « ${label} »` : 'Enregistrement supprimé';
  }
  if (entry.action === 'UPDATE' && entry.old_data && entry.new_data) {
    const changed: string[] = [];
    Object.keys(entry.new_data).forEach((k) => {
      if (k === 'updated_at') return;
      const oldV = entry.old_data[k];
      const newV = entry.new_data[k];
      if (JSON.stringify(oldV) !== JSON.stringify(newV)) {
        changed.push(k);
      }
    });
    if (changed.length === 0) return 'Aucun changement détecté';
    if (changed.length <= 3) return `Champs modifiés : ${changed.join(', ')}`;
    return `${changed.length} champs modifiés (${changed.slice(0, 3).join(', ')}…)`;
  }
  return '—';
}

export default function Journal() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tableFilter, setTableFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);
    if (!error && data) setEntries(data as AuditEntry[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (tableFilter !== 'all' && e.table_name !== tableFilter) return false;
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const blob = `${e.user_email || ''} ${e.table_name} ${describeChanges(e)} ${JSON.stringify(e.new_data || e.old_data || {})}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [entries, search, tableFilter, actionFilter]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading text-amber-400 flex items-center gap-2">
            <ScrollText size={24} strokeWidth={1.5} /> Journal des modifications
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Historique des actions sur prestations, clients, artistes et factures (500 dernières entrées).
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="bg-slate-900 border-slate-700 hover:bg-slate-800">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
        </Button>
      </div>

      {/* Filtres */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Rechercher (utilisateur, contenu...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-950 border-slate-800 text-slate-100"
          />
        </div>
        <Select value={tableFilter} onValueChange={setTableFilter}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-100">
            <Filter size={14} className="text-slate-500" />
            <SelectValue placeholder="Table" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            <SelectItem value="all">Toutes les tables</SelectItem>
            <SelectItem value="prestations">Prestations</SelectItem>
            <SelectItem value="clients">Clients</SelectItem>
            <SelectItem value="artistes">Artistes</SelectItem>
            <SelectItem value="factures">Factures</SelectItem>
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-100">
            <Filter size={14} className="text-slate-500" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            <SelectItem value="all">Toutes les actions</SelectItem>
            <SelectItem value="INSERT">Créations</SelectItem>
            <SelectItem value="UPDATE">Modifications</SelectItem>
            <SelectItem value="DELETE">Suppressions</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tableau */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/80 border-b border-slate-800 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Utilisateur</th>
                <th className="px-4 py-3 text-left">Table</th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">Détails</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">Chargement…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">Aucune entrée trouvée.</td></tr>
              )}
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{e.user_email || <span className="italic text-slate-600">système</span>}</td>
                  <td className="px-4 py-3 text-slate-200">{TABLE_LABELS[e.table_name] || e.table_name}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={ACTION_COLORS[e.action] || 'bg-slate-700/30 text-slate-300'}>
                      {ACTION_LABELS[e.action] || e.action}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-300 max-w-md truncate">{describeChanges(e)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(e)} className="text-xs text-amber-400 hover:text-amber-300 underline">
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal détails */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900">
              <h2 className="text-lg font-heading text-amber-400">Détail de l'entrée</h2>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-200">✕</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500 text-xs">Date :</span> <div className="text-slate-200">{new Date(selected.created_at).toLocaleString('fr-FR')}</div></div>
                <div><span className="text-slate-500 text-xs">Utilisateur :</span> <div className="text-slate-200">{selected.user_email || '—'}</div></div>
                <div><span className="text-slate-500 text-xs">Table :</span> <div className="text-slate-200">{TABLE_LABELS[selected.table_name] || selected.table_name}</div></div>
                <div><span className="text-slate-500 text-xs">Action :</span> <div><Badge className={ACTION_COLORS[selected.action]}>{ACTION_LABELS[selected.action]}</Badge></div></div>
              </div>
              {selected.old_data && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Données avant :</div>
                  <pre className="bg-slate-950 border border-slate-800 rounded p-3 text-xs text-slate-300 overflow-auto max-h-60">
                    {JSON.stringify(selected.old_data, null, 2)}
                  </pre>
                </div>
              )}
              {selected.new_data && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Données après :</div>
                  <pre className="bg-slate-950 border border-slate-800 rounded p-3 text-xs text-slate-300 overflow-auto max-h-60">
                    {JSON.stringify(selected.new_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
